import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Button, Grid, MenuItem, TextField, Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DataGrid } from '@mui/x-data-grid';
import { trTR } from '@mui/x-data-grid/locales';
import SearchIcon from '@mui/icons-material/Search';
import DownloadIcon from '@mui/icons-material/Download';
import axios from 'src/utils/axios';
import ExcelFileCreator from 'src/components/ExcelFileCreator';

import SectionCard from 'src/components/BpoMutabakat/SectionCard';
import InfoNote from 'src/components/BpoMutabakat/InfoNote';
import { getLevelColor } from 'src/components/BpoMutabakat/colors';
import { formatDecimal, formatNumber } from 'src/components/BpoMutabakat/format';
import { reportName } from 'src/components/BpoMutabakat/reports';

/** Küçük ölçü kutusu — sekme içinde iki yerde kullanılıyor. */
const Stat = ({ label, value, unit }) => (
  <Box
    sx={(theme) => ({
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 1,
      px: 1.75,
      py: 1.25,
      backgroundColor:
        theme.palette.mode === 'dark' ? 'rgba(255,255,255,.03)' : theme.palette.grey[50]
    })}
  >
    <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>{label}</Typography>
    <Typography
      sx={{ fontSize: '1.25rem', fontWeight: 500, fontVariantNumeric: 'tabular-nums', mt: 0.25 }}
    >
      {value}
      {unit && (
        <Typography component="span" sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
          {' '}
          {unit}
        </Typography>
      )}
    </Typography>
  </Box>
);

Stat.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
  unit: PropTypes.string
};

const CallDetail = ({ date }) => {
  const theme = useTheme();

  /* ------------------------------------------- en çok aranan numaralar (RT 12) */
  const [numberSummary, setNumberSummary] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [numbersLoading, setNumbersLoading] = useState(true);

  /* ----------------------------------------------------- çağrı kayıtları (RT 13) */
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [recordsLoading, setRecordsLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 0, pageSize: 50 });
  const [channels, setChannels] = useState([]);
  const [channel, setChannel] = useState('');
  const [numberQuery, setNumberQuery] = useState('');
  const [appliedFilters, setAppliedFilters] = useState({ channel: '', number: '' });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!date) return undefined;
    let cancelled = false;
    setNumbersLoading(true);

    Promise.allSettled([
      axios.get(`/GetBpoMutabakatDialedNumbers/${date}`),
      axios.get(`/GetBpoMutabakatChannels/${date}`)
    ])
      .then(([numaraSonuc, kanalSonuc]) => {
        if (cancelled) return;
        const body = numaraSonuc.status === 'fulfilled' ? numaraSonuc.value?.data : null;
        setNumberSummary(body?.summary || null);
        setNumbers(Array.isArray(body?.items) ? body.items : []);
        setChannels(
          kanalSonuc.status === 'fulfilled' && Array.isArray(kanalSonuc.value?.data)
            ? kanalSonuc.value.data
            : []
        );
      })
      .finally(() => {
        if (!cancelled) setNumbersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [date]);

  /* Sayfalama sunucu tarafında: 40 binin üzerindeki satır tarayıcıya inmez. */
  const loadRecords = useCallback(() => {
    if (!date) return undefined;
    let cancelled = false;

    setRecordsLoading(true);
    setError('');

    const query = new URLSearchParams({
      Sayfa: pagination.page,
      Boyut: pagination.pageSize
    });
    if (appliedFilters.channel) query.set('Kanal', appliedFilters.channel);
    if (appliedFilters.number) query.set('Numara', appliedFilters.number);

    axios
      .get(`/GetBpoMutabakatCallRecords/${date}?${query.toString()}`)
      .then((response) => {
        if (cancelled) return;
        const body = response?.data || {};
        setRecords(Array.isArray(body.items) ? body.items : []);
        setTotal(Number(body.total) || 0);
      })
      .catch((e) => {
        if (cancelled) return;
        setRecords([]);
        setTotal(0);
        setError(
          e?.response?.status === 404
            ? 'Çağrı kayıtları servisi henüz yayında değil.'
            : 'Çağrı kayıtları alınamadı.'
        );
      })
      .finally(() => {
        if (!cancelled) setRecordsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [date, pagination, appliedFilters]);

  useEffect(() => loadRecords(), [loadRecords]);

  const applyFilters = () => {
    setPagination((p) => ({ ...p, page: 0 }));
    setAppliedFilters({ channel, number: numberQuery.trim() });
  };

  const bad = getLevelColor(theme, 'bad');
  const warn = getLevelColor(theme, 'warn');
  const idle = getLevelColor(theme, 'empty');

  const numberColumns = [
    { field: 'id', headerName: '#', type: 'number', width: 70 },
    { field: 'number', headerName: 'Aranan numara', flex: 1, minWidth: 170 },
    {
      field: 'count',
      headerName: 'Çağrı adedi',
      type: 'number',
      width: 118,
      valueFormatter: (v) => formatNumber(v)
    },
    {
      field: 'minutes',
      headerName: 'Toplam süre',
      type: 'number',
      width: 122,
      valueFormatter: (v) => `${formatNumber(v)} dk`
    },
    {
      field: 'avgMinutes',
      headerName: 'Çağrı başına',
      type: 'number',
      width: 126,
      valueFormatter: (v) => `${formatDecimal(v)} dk`
    },
    {
      field: 'profile',
      headerName: 'Tekrar profili',
      width: 150,
      sortable: false,
      valueGetter: (value, row) => row.count,
      renderCell: (params) => {
        const adet = params.value || 0;
        const renk = adet >= 12 ? bad : adet >= 8 ? warn : idle;
        const etiket = adet >= 12 ? 'yoğun tekrar' : adet >= 8 ? 'tekrar' : 'normal';
        return (
          <Box
            component="span"
            sx={{
              px: 1,
              py: '2px',
              borderRadius: 5,
              fontSize: '0.72rem',
              fontWeight: 500,
              color: renk.text,
              backgroundColor: renk.bg
            }}
          >
            {etiket}
          </Box>
        );
      }
    }
  ];

  const recordColumns = [
    { field: 'id', headerName: 'Çağrı ID', type: 'number', width: 116 },
    { field: 'time', headerName: 'Saat', width: 100 },
    { field: 'caller', headerName: 'Arayan', flex: 1, minWidth: 150 },
    { field: 'called', headerName: 'Aranan', flex: 1, minWidth: 150 },
    { field: 'channel', headerName: 'Kanal', flex: 1, minWidth: 160 },
    { field: 'duration', headerName: 'Süre', width: 110 }
  ];

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <SectionCard
          title="En çok aranan numaralar"
          subtitle={`${reportName(12)} · ${formatNumber(numbers.length)} numara`}
          action={
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              disabled={numbersLoading || !numbers.length}
              onClick={() =>
                ExcelFileCreator(
                  numbers.map((r) => ({
                    Sıra: r.id,
                    'Aranan numara': r.number,
                    'Çağrı adedi': r.count,
                    'Toplam dk': r.minutes,
                    'Çağrı başına dk': r.avgMinutes
                  })),
                  `BPO Mutabakat - Aranan Numaralar ${date}`
                )
              }
            >
              Excel
            </Button>
          }
          padded={false}
        >
          <Box sx={{ p: 2, pb: 0 }}>
            <Box
              sx={{
                display: 'grid',
                gap: 1.5,
                gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))'
              }}
            >
              <Stat label="Tekil numara" value={formatNumber(numberSummary?.uniqueNumbers)} />
              <Stat label="Toplam çağrı" value={formatNumber(numberSummary?.totalCalls)} />
              <Stat label="3+ kez arayan" value={formatNumber(numberSummary?.repeatCallers)} />
              <Stat label="En çok arayan" value={formatNumber(numberSummary?.maxCalls)} unit="çağrı" />
              <Stat
                label="Numara başına"
                value={formatDecimal(
                  (numberSummary?.totalCalls || 0) / (numberSummary?.uniqueNumbers || 1)
                )}
                unit="çağrı"
              />
            </Box>
          </Box>
          <Box sx={{ height: 420, width: '100%', mt: 2 }}>
            <DataGrid
              rows={numbers}
              columns={numberColumns}
              loading={numbersLoading}
              density="compact"
              disableRowSelectionOnClick
              localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
              initialState={{ pagination: { paginationModel: { pageSize: 25 } } }}
              pageSizeOptions={[10, 25, 50, 100]}
              sx={{ border: 0 }}
            />
          </Box>
          <Box sx={{ p: 2 }}>
            <InfoNote>
              <b>Aynı numaranın gün içinde tekrar tekrar araması</b> çözülmemiş bir kaydın ya da
              yanlış yönlendirmenin işaretidir. Mutabakatta &ldquo;bu çağrılar tekil müşteri
              mi&rdquo; sorusunun cevabı burada. Numaralar KVKK gereği maskelenmiş gelir; ekran
              tekil numarayı değil tekrar davranışını gösterir.
            </InfoNote>
          </Box>
        </SectionCard>
      </Grid>

      <Grid size={12}>
        <SectionCard
          title="Çağrı kayıtları"
          subtitle={`${reportName(13)} · ${formatNumber(total)} kayıt`}
          padded={false}
        >
          <Box sx={{ p: 2, pb: 1 }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Arayan numara"
                  placeholder="0532…"
                  value={numberQuery}
                  onChange={(e) => setNumberQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') applyFilters();
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Kanal"
                  value={channel}
                  onChange={(e) => setChannel(e.target.value)}
                >
                  <MenuItem value="">Tümü</MenuItem>
                  {channels.map((c) => (
                    <MenuItem key={c.channel} value={c.channel}>
                      {c.channel}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid size={{ xs: 12, sm: 6, md: 2 }}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<SearchIcon />}
                  onClick={applyFilters}
                  disabled={recordsLoading}
                  sx={{ height: 40 }}
                >
                  Ara
                </Button>
              </Grid>
            </Grid>
          </Box>
          <Box sx={{ height: 560, width: '100%' }}>
            <DataGrid
              rows={records}
              columns={recordColumns}
              rowCount={total}
              loading={recordsLoading}
              paginationMode="server"
              paginationModel={pagination}
              onPaginationModelChange={setPagination}
              pageSizeOptions={[25, 50, 100]}
              density="compact"
              disableRowSelectionOnClick
              disableColumnFilter
              localeText={trTR.components.MuiDataGrid.defaultProps.localeText}
              sx={{ border: 0 }}
            />
          </Box>
          <Box sx={{ p: 2 }}>
            <InfoNote>
              {error ? (
                <b>{error}</b>
              ) : (
                <>
                  Günde 40 binin üzerinde kayıt olduğu için <b>sayfalama sunucu tarafında</b>
                  {' '}yapılır; tarayıcıya yalnızca görünen sayfa iner. Filtreler de sorguya
                  gider, bu yüzden sonuç sayısı filtreye göre değişir. Bu tabloda çağrı saati
                  ayrı bir kolondan değil <code>VERI_TARIHI</code> alanından okunur &mdash; bu
                  rapor tarih alanında saati de taşıyan tek rapordur.
                </>
              )}
            </InfoNote>
          </Box>
        </SectionCard>
      </Grid>
    </Grid>
  );
};

CallDetail.propTypes = {
  date: PropTypes.string
};

export default CallDetail;
